import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '+971 ', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const subjects = ['General Inquiry', 'Order Support', 'Returns & Refunds', 'Product Question', 'Wholesale Inquiry', 'Partnership'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '+971 ', subject: '', message: '' });
      setCharCount(0);
    } finally {
      setSubmitting(false);
    }
  };

  const [blobVisible, setBlobVisible] = useState(false);
  useEffect(() => { setBlobVisible(true); }, []);

  useEffect(() => {
    document.title = 'Contact AiShopio Dubai - WhatsApp, Email & Phone Support UAE';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Contact AiShopio Dubai support team via WhatsApp +971 50 000 0000, email hello@aishopio.ae or phone. Available Sat-Thu 9AM-9PM GST. Fast response for orders, returns & inquiries.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact AiShopio Dubai',
      description: 'Contact AiShopio Dubai support team via WhatsApp, email or phone.',
      url: `${siteUrl}/contact`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="contact"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'contact');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="contact"]')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30 relative overflow-hidden">
      {/* Floating Blob Backgrounds */}
      <div className={`absolute top-20 left-10 w-72 h-72 rounded-full opacity-20 pointer-events-none transition-all duration-1000 ${blobVisible ? 'scale-100' : 'scale-0'}`}
        style={{ background: 'radial-gradient(circle, #f97316, #ec4899)', filter: 'blur(60px)', animation: 'morphBg 8s ease-in-out infinite' }} />
      <div className={`absolute bottom-40 right-10 w-96 h-96 rounded-full opacity-15 pointer-events-none transition-all duration-1000 delay-300 ${blobVisible ? 'scale-100' : 'scale-0'}`}
        style={{ background: 'radial-gradient(circle, #f59e0b, #f97316)', filter: 'blur(80px)', animation: 'morphBg 10s ease-in-out infinite reverse' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent)', filter: 'blur(100px)' }} />

      <Header />

      <div className="pt-24 pb-8 px-4 sm:px-6 relative z-10">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-3">
            Get in Touch
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            We&apos;re here to help! Reach out via WhatsApp for the fastest response.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-600">Contact</span>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Contact Info */}
          <div className="space-y-4">
            {/* WhatsApp - Most Prominent */}
            <a
              href="https://wa.me/971500000000"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 sm:p-6 rounded-3xl text-white hover:scale-[1.02] transition-all duration-300 cursor-pointer overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #25d366, #128c7e)', boxShadow: '0 10px 40px rgba(37,211,102,0.3)' }}
            >
              <div className="flex items-center gap-3 sm:gap-4 mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-2xl bg-white/20 flex-shrink-0">
                  <i className="ri-whatsapp-line text-2xl sm:text-3xl"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white/80 text-sm font-medium">WhatsApp (Fastest)</p>
                  <p className="text-lg sm:text-xl font-black truncate">+971 50 000 0000</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 sm:px-4 py-2 w-fit">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse flex-shrink-0"></div>
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Replies within minutes</span>
              </div>
            </a>

            {/* Phone */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-orange-50">
                  <i className="ri-phone-line text-orange-500 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Phone</p>
                  <p className="font-bold text-gray-800">+971 4 000 0000</p>
                  <p className="text-xs text-gray-400">Sat–Thu: 9AM–9PM GST</p>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-pink-50">
                  <i className="ri-mail-line text-pink-500 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Email</p>
                  <p className="font-bold text-gray-800">hello@aishopio.ae</p>
                  <p className="text-xs text-gray-400">Response within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-50">
                  <i className="ri-map-pin-line text-amber-500 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Location</p>
                  <p className="font-bold text-gray-800">Dubai, UAE</p>
                  <p className="text-xs text-gray-400">Delivering across all 7 Emirates</p>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <i className="ri-time-line text-orange-500"></i> Business Hours
              </h3>
              <div className="space-y-2">
                {[
                  { day: 'Saturday – Thursday', hours: '9:00 AM – 9:00 PM' },
                  { day: 'Friday', hours: 'Closed' },
                ].map((h, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{h.day}</span>
                    <span className={`font-semibold ${h.hours === 'Closed' ? 'text-red-500' : 'text-gray-800'}`}>{h.hours}</span>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-2">All times in Gulf Standard Time (GST)</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-green-100 animate-bounceIn">
                    <i className="ri-check-line text-4xl text-green-500"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-800 mb-2">Message Sent!</h3>
                  <p className="text-gray-500 mb-6">We&apos;ll get back to you within 24 hours. For faster response, use WhatsApp!</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 rounded-full border-2 border-orange-500 text-orange-500 font-semibold hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Send Another Message
                    </button>
                    <a
                      href="https://wa.me/971500000000"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 rounded-full text-white font-semibold hover:scale-105 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      style={{ background: '#25d366' }}
                    >
                      <i className="ri-whatsapp-line"></i>Chat on WhatsApp
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-black text-gray-800 mb-6">Send us a Message</h2>
                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    data-readdy-form
                    id="contact-form"
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          placeholder="Your full name"
                          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={e => setForm({ ...form, email: e.target.value })}
                          placeholder="email@example.com"
                          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          placeholder="+971 50 000 0000"
                          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Subject *</label>
                        <select
                          name="subject"
                          required
                          value={form.subject}
                          onChange={e => setForm({ ...form, subject: e.target.value })}
                          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800 cursor-pointer"
                        >
                          <option value="">Select a subject</option>
                          {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
                        Message * <span className={`font-normal ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>({charCount}/500)</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        value={form.message}
                        onChange={e => { setForm({ ...form, message: e.target.value }); setCharCount(e.target.value.length); }}
                        placeholder="How can we help you today?"
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800 resize-none"
                      />
                      {charCount > 500 && <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || charCount > 500}
                      className="w-full py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-[1.02] hover:shadow-lg hover:shadow-orange-500/30 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin mr-2"></i>Sending...</>
                      ) : (
                        <><i className="ri-send-plane-line mr-2"></i>Send Message</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
